// src/pages/Forums.js – Forums list (4-column, hover definitions, pagination, breadcrumb)
// Build: 2026-05-05-visual-storytelling-protection-language-v1

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import {
  loadStoredStories,
  saveStoredStories,
  mergeStoredAndRemoteStories,
} from "../storyStorage";
import { api } from "../api";

// Forums list (canonical stored values)
const FORUMS = [
  "Action Forum",
  "Adventure Forum",
  "Children Forum",
  "Comedy Forum",
  "Crime Forum",
  "Fantasy Forum",
  "Historical Forum",
  "Inspirational Stories Forum",
  "Mystery Forum",
  "Poetry Forum",
  "Romance Forum",
  "Suspense / Thriller Forum",
  "Science Fiction Forum",
  "Faith & Inspiration Forum",
  "Humour Forum",
  "Non-Fiction Stories Forum",
  "Young Adult Forum",
  "Young Illustrators Gallery",
  "Comics & Illustrated Stories Forum",
  "NextScenes Originals",
  "Unclassified Forum",
];

// how many forum cards per page (4 columns x 3 rows)
const PAGE_SIZE = 12;

const FORUM_TEXT = {
  en: {
    breadcrumb_forums: "FORUMS",
    page_title: "Forums",
    page_subtitle:
      "Forums group storylines by genre. Choose a forum to see all active boards in that category on the Storylines page.",
    load_error: "Could not reach the server. Storyline counts may be incomplete.",
    no_storylines_yet: "No active storylines yet.",
    active_storyline_one: "active storyline.",
    active_storyline_many: "active storylines.",
    click_to_view: "Click to view these storylines on the Storylines page.",
    prev: "Prev",
    next: "Next",
    fallback_definition:
      "Stories in this forum should respect the values of imagination with conscience and explore this theme in any creative way.",
    hero_kicker: "NextScenes",
    hero_stat_label: "creative forums",
    forum_labels: {
      "Action Forum": "Action",
      "Adventure Forum": "Adventure",
      "Children Forum": "Children",
      "Comedy Forum": "Comedy",
      "Crime Forum": "Crime",
      "Fantasy Forum": "Fantasy",
      "Historical Forum": "Historical",
      "Inspirational Stories Forum": "Inspirational Stories",
      "Mystery Forum": "Mystery",
      "Poetry Forum": "Poetry",
      "Romance Forum": "Romance",
      "Suspense / Thriller Forum": "Suspense / Thriller",
      "Science Fiction Forum": "Science Fiction",
      "Faith & Inspiration Forum": "Faith & Inspiration",
      "Humour Forum": "Humour",
      "Non-Fiction Stories Forum": "Non-Fiction Stories",
      "Young Adult Forum": "Young Adult",
      "Young Illustrators Gallery": "Young Illustrators Gallery",
      "Comics & Illustrated Stories Forum": "Comics & Illustrated Stories",
      "NextScenes Originals": "NextScenes Originals",
      "Unclassified Forum": "Unclassified",
    },
    forum_definitions: {
      "Action Forum":
        "Fiction with horrifying and desperate situations. Characters may react in unpredictable ways, often facing danger and sometimes using weapons.",
      "Adventure Forum":
        "Stories where characters set out on a mission in unfamiliar surroundings – treasure hunts, legendary places and risky journeys that may succeed or fail.",
      "Children Forum":
        "Stories for young people that amuse, entertain and teach values like honesty, discipline, respect and self-esteem, told in simple, clear language.",
      "Comedy Forum":
        "Plots built with amusing characters and comic events. The main goal is to make people laugh using genuine humour and playful situations.",
      "Crime Forum":
        "Stories about fictional crimes – murder, robbery, kidnapping and more – often with detectives or curious people trying to solve the case.",
      "Fantasy Forum":
        "Tales of imaginary beings, planets, myths, folklore or supernatural forces, where contributors can truly let imagination run free.",
      "Historical Forum":
        "Stories that reimagine history, starting from real events but exploring how things could have unfolded differently.",
      "Inspirational Stories Forum":
        "Stories that touch the heart and celebrate the human spirit overcoming obstacles through willpower, determination and patience.",
      "Mystery Forum":
        "Plots centred on unexplained events – crimes, deaths, financial wrongdoing, disappearances – where contributors propose possible solutions.",
      "Poetry Forum":
        "Original poems expressing life, events, issues or feelings through the unique voice and perspective of a poet.",
      "Romance Forum":
        "Stories about relationships and love affairs – attraction, conflict, betrayal – showing how love grows, changes or breaks.",
      "Suspense / Thriller Forum":
        "Stories that keep readers in tension, blending fear with excitement, often mixed with crime, action or adventure.",
      "Science Fiction Forum":
        "Imaginative stories about future technology, space, alternate realities or scientific ‘what if’ ideas.",
      "Faith & Inspiration Forum":
        "Stories shaped by faith, hope and spiritual insight, showing how belief influences choices and daily life.",
      "Humour Forum":
        "Playful stories full of jokes, exaggeration and witty observations, written simply to amuse.",
      "Non-Fiction Stories Forum":
        "True-life narratives based on real people and events, told in story form while remaining faithful to the facts.",
      "Young Adult Forum":
        "Stories focused on teenagers and young adults, wrestling with identity, school, friendship, love and big decisions.",
      "Young Illustrators Gallery":
        "A protected, curated gallery for artwork created by children and young people. Children’s artwork must be submitted only by a parent, guardian, teacher, or approved adult. Do not include a child’s full name, face, school name, address, phone number, or private identifying detail. Children create. Adults submit. NextScenes moderates.",
      "Comics & Illustrated Stories Forum":
        "For comic creators, illustrators and visual storytellers. This space is for illustrated storytelling, comic series, panels, captions and visual narrative work.",
      "NextScenes Originals":
        "For platform-curated or in-house story projects, including featured comic series, official experiments and selected creative works.",
      "Unclassified Forum":
        "For stories that do not fit neatly into another forum but still honour NextScenes values and community rules.",
    },
  },

  fr: {
    breadcrumb_forums: "FORUMS",
    page_title: "Forums",
    page_subtitle:
      "Les forums regroupent les storylines par genre. Choisissez un forum pour voir toutes les publications actives de cette catégorie sur la page Storylines.",
    load_error:
      "Impossible de joindre le serveur. Les nombres de storylines peuvent être incomplets.",
    no_storylines_yet: "Aucune storyline active pour le moment.",
    active_storyline_one: "storyline active.",
    active_storyline_many: "storylines actives.",
    click_to_view:
      "Cliquez pour voir ces storylines sur la page Storylines.",
    prev: "Précédent",
    next: "Suivant",
    fallback_definition:
      "Les histoires de ce forum doivent respecter les valeurs d’imagination avec conscience et explorer ce thème de façon créative.",
    hero_kicker: "NextScenes",
    hero_stat_label: "forums créatifs",
    forum_labels: {
      "Action Forum": "Action",
      "Adventure Forum": "Aventure",
      "Children Forum": "Enfants",
      "Comedy Forum": "Comédie",
      "Crime Forum": "Crime",
      "Fantasy Forum": "Fantaisie",
      "Historical Forum": "Historique",
      "Inspirational Stories Forum": "Histoires inspirantes",
      "Mystery Forum": "Mystère",
      "Poetry Forum": "Poésie",
      "Romance Forum": "Romance",
      "Suspense / Thriller Forum": "Suspense / Thriller",
      "Science Fiction Forum": "Science-fiction",
      "Faith & Inspiration Forum": "Foi et inspiration",
      "Humour Forum": "Humour",
      "Non-Fiction Stories Forum": "Récits non romanesques",
      "Young Adult Forum": "Jeunes adultes",
      "Young Illustrators Gallery": "Galerie des jeunes illustrateurs",
      "Comics & Illustrated Stories Forum": "Bandes dessinées et récits illustrés",
      "NextScenes Originals": "Créations originales NextScenes",
      "Unclassified Forum": "Non classé",
    },
    forum_definitions: {
      "Action Forum":
        "Fictions marquées par des situations terribles et désespérées. Les personnages peuvent réagir de manière imprévisible, affrontant souvent le danger et parfois les armes.",
      "Adventure Forum":
        "Histoires où les personnages partent en mission dans des lieux inconnus : chasses au trésor, lieux légendaires et voyages risqués qui peuvent réussir ou échouer.",
      "Children Forum":
        "Histoires pour les jeunes qui amusent, divertissent et enseignent des valeurs comme l’honnêteté, la discipline, le respect et l’estime de soi, dans une langue simple et claire.",
      "Comedy Forum":
        "Intrigues construites autour de personnages amusants et d’événements comiques. Le but principal est de faire rire par un humour authentique et des situations ludiques.",
      "Crime Forum":
        "Histoires de crimes fictifs — meurtre, vol, enlèvement et autres — souvent avec des enquêteurs ou des curieux cherchant à résoudre l’affaire.",
      "Fantasy Forum":
        "Récits d’êtres imaginaires, de planètes, de mythes, de folklore ou de forces surnaturelles, où l’imagination peut pleinement s’exprimer.",
      "Historical Forum":
        "Histoires qui réinventent l’histoire à partir de faits réels, en explorant comment les choses auraient pu se dérouler autrement.",
      "Inspirational Stories Forum":
        "Histoires qui touchent le cœur et célèbrent l’esprit humain surmontant les obstacles par la volonté, la détermination et la patience.",
      "Mystery Forum":
        "Intrigues centrées sur des événements inexpliqués — crimes, décès, malversations financières, disparitions — où les contributeurs proposent des solutions possibles.",
      "Poetry Forum":
        "Poèmes originaux exprimant la vie, les événements, les enjeux ou les sentiments à travers la voix et la sensibilité propres du poète.",
      "Romance Forum":
        "Histoires sur les relations et les amours — attirance, conflit, trahison — montrant comment l’amour grandit, change ou se brise.",
      "Suspense / Thriller Forum":
        "Histoires qui maintiennent le lecteur en tension, mêlant peur et excitation, souvent avec du crime, de l’action ou de l’aventure.",
      "Science Fiction Forum":
        "Histoires imaginatives sur les technologies futures, l’espace, les réalités alternatives ou les idées scientifiques hypothétiques.",
      "Faith & Inspiration Forum":
        "Histoires façonnées par la foi, l’espérance et l’inspiration spirituelle, montrant comment la croyance influence les choix et la vie quotidienne.",
      "Humour Forum":
        "Histoires légères pleines de plaisanteries, d’exagérations et d’observations spirituelles, écrites simplement pour amuser.",
      "Non-Fiction Stories Forum":
        "Récits de vie vraie fondés sur des personnes et des événements réels, racontés sous forme d’histoire tout en restant fidèles aux faits.",
      "Young Adult Forum":
        "Histoires centrées sur les adolescents et les jeunes adultes, confrontés à l’identité, à l’école, à l’amitié, à l’amour et aux grandes décisions.",
      "Young Illustrators Gallery":
        "Une galerie protégée et organisée pour les œuvres créées par des enfants et des jeunes. Les œuvres d’enfants doivent être soumises uniquement par un parent, un tuteur, un enseignant ou un adulte autorisé. N’indiquez pas le nom complet de l’enfant, son visage, le nom de son école, son adresse, son numéro de téléphone ou tout autre détail privé permettant de l’identifier. Les enfants créent. Les adultes soumettent. NextScenes modère.",
      "Comics & Illustrated Stories Forum":
        "Pour les créateurs de bandes dessinées, les illustrateurs et les conteurs visuels. Cet espace est destiné aux récits illustrés, aux séries dessinées, aux planches, aux légendes et aux œuvres narratives visuelles.",
      "NextScenes Originals":
        "Pour les projets sélectionnés ou créés par NextScenes, y compris les séries dessinées mises en avant, les expérimentations officielles et les œuvres créatives choisies.",
      "Unclassified Forum":
        "Pour les histoires qui n’entrent pas clairement dans une autre catégorie mais respectent malgré tout les valeurs et les règles de la communauté NextScenes.",
    },
  },
};

function getLang() {
  try {
    const value = String(window.localStorage.getItem("ns.lang") || "").trim().toLowerCase();
    return value === "fr" ? "fr" : "en";
  } catch {
    return "en";
  }
}

function getForumLabel(forum, ui) {
  return ui.forum_labels?.[forum] || forum.replace(/\s+Forum$/i, "").trim();
}

export default function Forums({ onSelectForum, breadcrumb, onNavigatePage }) {
  const [stories, setStories] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [hoverForum, setHoverForum] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeForum, setActiveForum] = useState("");
  const [lang, setLang] = useState(getLang());

  const ui = useMemo(() => FORUM_TEXT[lang] || FORUM_TEXT.en, [lang]);

  useEffect(() => {
    const stored = loadStoredStories() || [];
    setStories(stored);
    fetchRemote(stored);

    try {
      const storedForum = window.localStorage.getItem("ns.selectedForum") || "";
      if (storedForum) {
        setActiveForum(storedForum);
        const idx = FORUMS.indexOf(storedForum);
        if (idx >= 0) {
          const neededPage = Math.floor(idx / PAGE_SIZE) + 1;
          if (neededPage !== 1) setCurrentPage(neededPage);
        }
      }
    } catch {
      // ignore
    }

    function syncLang() {
      setLang((current) => {
        const next = getLang();
        return current === next ? current : next;
      });
    }

    function handleStorage(e) {
      if (!e) return;
      if (e.key === "ns.lang") syncLang();
    }

    // The native storage event does not fire in the same browser tab that changed localStorage.
    // These listeners make the Forums page respond reliably when the app language toggle is used.
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", syncLang);
    window.addEventListener("pageshow", syncLang);
    document.addEventListener("visibilitychange", syncLang);
    window.addEventListener("ns.lang.changed", syncLang);
    window.addEventListener("ns-lang-changed", syncLang);
    window.addEventListener("languagechange", syncLang);

    const langPoll = window.setInterval(syncLang, 600);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", syncLang);
      window.removeEventListener("pageshow", syncLang);
      document.removeEventListener("visibilitychange", syncLang);
      window.removeEventListener("ns.lang.changed", syncLang);
      window.removeEventListener("ns-lang-changed", syncLang);
      window.removeEventListener("languagechange", syncLang);
      window.clearInterval(langPoll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeForum) return;
    const el = document.getElementById(`forum-card-${activeForum}`);
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      try {
        window.localStorage.removeItem("ns.selectedForum");
      } catch {
        // ignore
      }
    }
  }, [activeForum, currentPage]);

  async function fetchRemote(stored) {
    try {
      setLoadError("");
      const data = await api("/api/stories", { method: "GET" });

      if (Array.isArray(data) && data.length > 0) {
        const merged = mergeStoredAndRemoteStories(stored, data);
        setStories(merged);
        saveStoredStories(merged);
      }
    } catch (err) {
      console.error("Forums: error loading stories:", err.message);
      if (!stored || stored.length === 0) {
        setLoadError(ui.load_error);
      }
    }
  }

  useEffect(() => {
    if (!loadError) return;
    setLoadError(ui.load_error);
  }, [ui, loadError]);

  const countsByForum = FORUMS.reduce((acc, forumName) => {
    acc[forumName] = 0;
    return acc;
  }, {});

  stories.forEach((s) => {
    if (!s) return;
    const f = s.forum || "Unclassified Forum";
    if (countsByForum[f] === undefined) countsByForum[f] = 0;
    countsByForum[f] += 1;
  });

  function handleClickForum(name) {
    try {
      window.localStorage.setItem("ns.selectedForum", name);
    } catch {
      // ignore
    }

    if (typeof onSelectForum === "function") onSelectForum(name);
    if (typeof onNavigatePage === "function") onNavigatePage("storylines");
  }

  const totalPages = Math.ceil(FORUMS.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pageForums = FORUMS.slice(startIndex, startIndex + PAGE_SIZE);

  function goToPage(page) {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="ns-page forums-page">
      {breadcrumb && breadcrumb.length > 0 ? (
        <div className="ns-breadcrumb-trail forums-breadcrumb-trail">
          {breadcrumb.map((seg, idx) => (
            <span key={seg.label}>
              {idx > 0 && <span className="ns-breadcrumb-separator">/</span>}
              {seg.page && onNavigatePage ? (
                <button className="ns-breadcrumb-link" onClick={() => onNavigatePage(seg.page)}>
                  {seg.label}
                </button>
              ) : (
                <span className="ns-breadcrumb-current">{seg.label}</span>
              )}
            </span>
          ))}
        </div>
      ) : (
        <div className="ns-breadcrumb forums-breadcrumb-trail">{ui.breadcrumb_forums}</div>
      )}

      <header
        className="ns-identity-hero"
        aria-labelledby="forums-page-title"
        style={{
          backgroundImage:
            'linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url("/images/storyhub/storyhub-background.png")',
        }}
      >
        <div className="ns-identity-hero-copy">
          <p className="ns-identity-kicker">{ui.hero_kicker}</p>
          <h1 id="forums-page-title" className="ns-identity-title">{ui.page_title}</h1>
          <p className="ns-identity-subtitle">{ui.page_subtitle}</p>

          {loadError ? (
            <div className="ns-identity-actions">
              <span className="ns-identity-status is-error">{loadError}</span>
            </div>
          ) : null}
        </div>

        <div className="ns-identity-hero-stat" aria-label={ui.hero_stat_label}>
          <span className="ns-identity-stat-number">{FORUMS.length}</span>
          <span className="ns-identity-stat-label">{ui.hero_stat_label}</span>
        </div>
      </header>

      <section className="ns-column forums-shelf" aria-label={ui.page_title}>
        <div className="forums-grid">
          {pageForums.map((forum) => {
            const count = countsByForum[forum] || 0;
            const hovered = hoverForum === forum;
            const isActive = activeForum === forum;
            const definition = ui.forum_definitions?.[forum] || ui.fallback_definition;

            return (
              <article
                key={forum}
                id={`forum-card-${forum}`}
                className={"ns-card ns-clickable forum-card" + (isActive ? " ns-card-active forum-card-active" : "") + (hovered ? " forum-card-hovered" : "")}
                onClick={() => handleClickForum(forum)}
                onMouseEnter={() => setHoverForum(forum)}
                onMouseLeave={() => setHoverForum("")}
              >
                <div className="forum-card-topline" aria-hidden="true" />
                <h3 className="ns-card-title forum-card-title">{getForumLabel(forum, ui)}</h3>

                <p className="ns-card-meta forum-count-text">
                  {count === 0
                    ? ui.no_storylines_yet
                    : `${count} ${count === 1 ? ui.active_storyline_one : ui.active_storyline_many}`}
                </p>

                <p className="ns-card-meta forum-card-action-text">{ui.click_to_view}</p>

                {hovered ? <div className="forum-definition">{definition}</div> : null}
              </article>
            );
          })}
        </div>

        {totalPages > 1 ? (
          <div className="forums-pagination">
            <button
              className="forums-page-nav"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ‹ {ui.prev}
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={"forums-page-number" + (page === currentPage ? " forums-page-number-active" : "")}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="forums-page-nav"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {ui.next} ›
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
